import React, { useState } from "react";
import { api } from "@/lib/api";
import { Instagram, Youtube, Music, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact({ config }) {
  const [form, setForm] = useState({
    name: "", email: "", song_title: "", reference_link: "",
    target_genre: "", budget: "", deadline: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.notes.trim()) {
      toast.error("Nombre, email y mensaje son obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/remix-requests", form);
      toast.success("Petición enviada. Te responderé pronto.");
      setForm({ name: "", email: "", song_title: "", reference_link: "", target_genre: "", budget: "", deadline: "", notes: "" });
    } catch {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    } finally { setSubmitting(false); }
  };

  const socials = [
    { name: "Instagram", href: config?.instagram, icon: Instagram },
    { name: "YouTube", href: config?.youtube, icon: Youtube },
    { name: "Spotify", href: config?.spotify, icon: Music },
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <div className="mb-10">
        <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-2">Colaboraciones</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white">
          Contacto & <span className="text-red-600">Remixes</span>
        </h1>
        <p className="text-white/50 mt-4 max-w-2xl">
          ¿Quieres un remix o mashup personalizado? Escríbeme desde el formulario o encuéntrame en mis redes.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 order-2 lg:order-1">
          <form
            data-testid="remix-request-form"
            onSubmit={submit}
            className="bg-[#111116] border border-white/5 rounded-2xl p-6 sm:p-8 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nombre" testid="form-name">
                <input required data-testid="form-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input" />
              </Field>
              <Field label="Email" testid="form-email">
                <input required type="email" data-testid="form-email-input" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input" />
              </Field>
            </div>
            <Field label="Mensaje">
              <textarea required data-testid="form-notes-input" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={6} className="input resize-none" placeholder="Cuéntame qué remix o mashup quieres: canción, estilo, referencias, fecha... lo que necesites." />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="remix-request-submit-button"
              className="w-full btn-red text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Enviando..." : "Enviar solicitud"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 order-1 lg:order-2 space-y-4">
          <div className="bg-[#111116] border border-white/5 rounded-2xl p-6">
            <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-3">Sígueme en</div>
            <div className="space-y-2">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href || "#"}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`social-${s.name.toLowerCase()}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-white/5 hover:border-red-600/50 hover:bg-red-600/5 transition-all group"
                >
                  <s.icon className="w-5 h-5 text-red-500 group-hover:scale-110 transition-transform" />
                  <span className="font-semibold text-white">{s.name}</span>
                </a>
              ))}
            </div>
          </div>

          {config?.contact_email && (
            <div className="bg-[#111116] border border-white/5 rounded-2xl p-6">
              <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-2">Email directo</div>
              <a href={`mailto:${config.contact_email}`} className="text-white hover:text-red-500 font-semibold">
                {config.contact_email}
              </a>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .input { width:100%; background: #16161D; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px 14px; color: white; font-size: 14px; }
        .input:focus { outline: none; border-color: rgba(229,9,20,0.5); box-shadow: 0 0 0 3px rgba(229,9,20,0.12); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
